import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import * as Yup from "yup";

import { ModalContainer } from "../PageComponents";
import ProfileImage from "../../../src/assets/images/ProfileImage.svg";
import { LabelInput } from "../Reusable/Layout";
import { GamerTags } from "./GamerTags";
import { PurchaseTable } from "./PurchaseTable";
import { CloseIcon } from "../../icons/promotions";
import { Dropdown } from "../../assets/Icons";
import { useAppDispatch, useAppSelector } from "../../hooks/hooks";
import { updateUser, fetchUserById } from "../../features/auth/store/userSlice";
import { Transaction, UserDetailModalInput, UserFormData } from "./types";

const userValidationSchema = Yup.object().shape({
  email: Yup.string()
    .email("Invalid email address")
    .required("Email is required"),
  phone_number: Yup.string()
    .min(10, "Phone number must be at least 10 characters")
    .max(15, "Phone number must not exceed 15 characters")
    .required("Phone number is required"),
  status: Yup.string().required("Status is required"),
  role: Yup.string().required("Role is required"),
});

export const formatMetricValue = (
  value: string | number | null | undefined,
) => {
  if (value === undefined || value === null || value === "") return "0";

  const numericValue =
    typeof value === "string" ? Number.parseFloat(value) : value;

  if (Number.isNaN(numericValue)) return String(value);

  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(numericValue);
};

export const UserDetailModal = ({
  userId,
  showTabs = true,
  tabItems = ["Details", "FAQ"],
  onClose,
  isOpen,
}: UserDetailModalInput) => {
  const dispatch = useAppDispatch();
  const firstTab = tabItems[0] || "Details";
  const [toggle, setToggle] = useState<string>(firstTab);
  const [userPurchases, setUserPurchases] = useState<Transaction[]>([]);
  const [purchasesLoading, setPurchasesLoading] = useState(false);
  const [purchaseRefreshKey, setPurchaseRefreshKey] = useState(0);
  const [purchaseFilters, setPurchaseFilters] = useState({
    source: "active",
    start_date: "",
    end_date: "",
    status: "",
    type: "",
  });
  const [coinAdjustmentAmount, setCoinAdjustmentAmount] = useState("");
  const [coinAdjustmentDescription, setCoinAdjustmentDescription] =
    useState("");
  const [coinAdjustmentLoading, setCoinAdjustmentLoading] = useState(false);
  const { selectedUser, loading, error } = useAppSelector(
    (state) => state.users,
  );
  const token = useAppSelector((state) => state.auth.token);

  const [formData, setFormData] = useState<UserFormData>({
    username: "",
    country: "",
    firstName: "",
    lastName: "",
    email: "",
    phone_number: "",
    birthDate: "",
    status: "",
    role: "",
  });

  // Fetch user data when modal opens or userId changes
  useEffect(() => {
    if (isOpen && userId) {
      setToggle(firstTab);
      dispatch(fetchUserById(userId));
    }
  }, [dispatch, firstTab, isOpen, userId]);

  // Update form data when user data is loaded
  useEffect(() => {
    if (selectedUser) {
      setFormData({
        username: selectedUser.username || "",
        country: selectedUser.country || "",
        firstName: selectedUser.firstname || "",
        lastName: selectedUser.lastname || "",
        email: selectedUser.email || "",
        phone_number: selectedUser.phone_number || "",
        birthDate: selectedUser.birth_date || selectedUser.birthDate || "",
        status: selectedUser.status || "",
        role: selectedUser.role || "",
      });
    }
  }, [selectedUser]);

  useEffect(() => {
    if (!isOpen) {
      setCoinAdjustmentAmount("");
      setCoinAdjustmentDescription("");
      setCoinAdjustmentLoading(false);
    }
  }, [isOpen]);

  const handleInputChange = (name: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSaveChanges = async () => {
    try {
      // Validate form data using Yup
      await userValidationSchema.validate(formData, { abortEarly: false });

      // Prepare data for API
      const updateData = {
        email: formData.email,
        phone_number: formData.phone_number,
        status: formData.status,
        role: formData.role,
        firstname: formData.firstName,
        lastname: formData.lastName,
        username: formData.username,
        country: formData.country,
        birth_date: formData.birthDate,
      };

      await dispatch(
        updateUser({ user_id: userId, data: updateData }),
      ).unwrap();
      toast.success("User updated successfully!");
    } catch (error: any) {
      if (error.inner) {
        // Yup validation errors
        const validationErrors = error.inner
          .map((err: any) => err.message)
          .join(", ");
        toast.error(`Validation errors: ${validationErrors}`);
      } else {
        // API or other errors
        toast.error("Failed to update user");
      }
    }
  };

  const handleCoinAdjustment = async (action: "credit" | "debit") => {
    if (!token) {
      toast.error("Unauthorized");
      return;
    }

    const amount = Number.parseFloat(coinAdjustmentAmount);

    if (!Number.isFinite(amount) || amount <= 0) {
      toast.error("Enter a valid DG Coin amount");
      return;
    }

    setCoinAdjustmentLoading(true);

    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await axios.post(
        `${apiUrl}/users/${userId}/dg-coins/adjust`,
        {
          action,
          amount,
          description: coinAdjustmentDescription.trim() || undefined,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      toast.success(
        response.data?.message ||
          `User DG Coin wallet ${action === "credit" ? "credited" : "debited"} successfully`,
      );
      setCoinAdjustmentAmount("");
      setCoinAdjustmentDescription("");
      dispatch(fetchUserById(userId));
      setPurchaseRefreshKey((prev) => prev + 1);
    } catch (adjustError: any) {
      toast.error(
        adjustError?.response?.data?.message ||
          "Failed to update DG Coin wallet",
      );
    } finally {
      setCoinAdjustmentLoading(false);
    }
  };

  useEffect(() => {
    const fetchUserPurchases = async () => {
      if (!isOpen || toggle !== "Purchases" || !userId || !token) {
        return;
      }

      setPurchasesLoading(true);

      try {
        const apiUrl = process.env.REACT_APP_API_URL;
        const endpoint =
          purchaseFilters.source === "archived"
            ? "/transactions/archived"
            : "/transactions";
        const response = await axios.get(`${apiUrl}${endpoint}`, {
          params: {
            user_id: userId,
            per_page: 100,
            start_date: purchaseFilters.start_date || undefined,
            end_date: purchaseFilters.end_date || undefined,
            status: purchaseFilters.status || undefined,
            type: purchaseFilters.type || undefined,
          },
          headers: { Authorization: `Bearer ${token}` },
        });

        setUserPurchases(response.data?.data?.data ?? []);
      } catch (fetchError) {
        console.error("Failed to fetch user transactions", fetchError);
        setUserPurchases([]);
      } finally {
        setPurchasesLoading(false);
      }
    };

    fetchUserPurchases();
  }, [isOpen, toggle, token, userId, purchaseFilters, purchaseRefreshKey]);

  if (!isOpen) return null;

  // Show loading state
  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg">
          <p className="text-center">Loading user details...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error && !selectedUser) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg">
          <p className="text-center text-red-500 mb-4">
            Error loading user details
          </p>
          <div className="flex gap-2 justify-center">
            <button
              onClick={() => dispatch(fetchUserById(userId))}
              className="px-4 py-2 bg-blue-500 text-white rounded-md"
            >
              Retry
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-500 text-white rounded-md"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  const renderContent = () => {
    switch (toggle) {
      case "Personal Information":
        return (
          <div className="mt-4 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <LabelInput
                  type="text"
                  value={formData.username}
                  placeholder="Username"
                  name="username"
                  onChange={(e) =>
                    handleInputChange("username", e.target.value)
                  }
                />
              </div>
              <div>
                <LabelInput
                  type="text"
                  value={formData.country}
                  placeholder="Country"
                  name="country"
                  onChange={(e) => handleInputChange("country", e.target.value)}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <LabelInput
                  type="text"
                  value={formData.firstName}
                  placeholder="First Name"
                  name="firstName"
                  onChange={(e) =>
                    handleInputChange("firstName", e.target.value)
                  }
                />
              </div>
              <div>
                <LabelInput
                  type="text"
                  value={formData.lastName}
                  placeholder="Last Name"
                  name="lastName"
                  onChange={(e) =>
                    handleInputChange("lastName", e.target.value)
                  }
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <LabelInput
                  type="email"
                  value={formData.email}
                  placeholder="Email Address"
                  name="email"
                  onChange={(e) => handleInputChange("email", e.target.value)}
                />
              </div>
              <div>
                <LabelInput
                  type="tel"
                  value={formData.phone_number}
                  placeholder="Phone Number"
                  name="phone"
                  onChange={(e) =>
                    handleInputChange("phone_number", e.target.value)
                  }
                />
              </div>
            </div>
            <div>
              <LabelInput
                type="date"
                value={formData.birthDate}
                placeholder="Birth Date"
                name="birthDate"
                onChange={(e) => handleInputChange("birthDate", e.target.value)}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <select
                  value={formData.status}
                  onChange={(e) => handleInputChange("status", e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                  <option value="">Select Status</option>
                  <option value="active">Active</option>
                  <option value="deleted">Deleted</option>
                  <option value="suspended">Suspended</option>
                </select>
              </div>
              <div>
                <select
                  value={formData.role}
                  onChange={(e) => handleInputChange("role", e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                  <option value="">Select Role</option>
                  <option value="user">User</option>
                  <option value="customer_service">
                    Admin (Default: Customer Service)
                  </option>
                </select>
              </div>
            </div>

            <div className="flex justify-end">
              <button
                onClick={handleSaveChanges}
                disabled={loading}
                className="border border-[#D0D5DD] dark:border-[#FFFFFF1A] px-4 py-2 rounded-md text-xs bp:text-sm dark:text-white dark:bg-[#FFFFFF1A] hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
              >
                {loading ? "Saving..." : "Save changes"}
              </button>
            </div>
          </div>
        );
      case "Gamer Tags":
        return <GamerTags />;
      case "Purchases":
        return (
          <div className="mt-4 space-y-4">
            <PurchasesFilter
              filters={purchaseFilters}
              loading={purchasesLoading}
              onFilterChange={(key, value) =>
                setPurchaseFilters((prev) => ({ ...prev, [key]: value }))
              }
              onClearFilters={() =>
                setPurchaseFilters({
                  source: "active",
                  start_date: "",
                  end_date: "",
                  status: "",
                  type: "",
                })
              }
            />
            <PurchaseTable
              purchases={userPurchases}
              loading={purchasesLoading}
            />
          </div>
        );
    }
  };

  return (
    <div>
      <ModalContainer
        title="User Details"
        closeIcon={
          <button onClick={onClose}>
            <CloseIcon />
          </button>
        }
      >
        <div className="bg-BG-light dark:bg-BG-dark dark:text-white flex items-center p-4 rounded-md mb-4">
          <div className="mr-4">
            <img
              src={selectedUser?.profile_image || ProfileImage}
              alt="Profile"
              className="w-[160px] h-[160px] rounded-full"
            />
          </div>

          <div>
            <p className="font-bold text-[40px] ">
              {selectedUser
                ? `${selectedUser.firstname || ""} ${
                    selectedUser.lastname || ""
                  }`.trim() || "N/A"
                : "Loading..."}
            </p>
            <div className="flex items-center gap-4 ">
              <p className="text-sm">{selectedUser?.username || "N/A"}</p>
              <span className="text-lg">|</span>
              <p className="text-sm">
                Joining date:{" "}
                {selectedUser?.created_at
                  ? new Date(selectedUser.created_at).toLocaleDateString()
                  : "N/A"}
              </p>
            </div>
            <div className="bg-amber-50 text-amber-500 rounded-lg w-32">
              <p className="p-2 text-base">
                {selectedUser?.status || "Unknown"}
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 bp:grid-cols-4 gap-3 mb-4">
          <div className="rounded-lg border border-[#EAECF0] dark:border-[#FFFFFF1A] bg-white dark:bg-[#FFFFFF0A] p-4">
            <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
              DG Coin Balance
            </p>
            <p className="mt-2 text-xl font-semibold dark:text-white">
              {formatMetricValue(
                selectedUser?.dg_coin_wallet_balance ||
                  selectedUser?.dgcoinwallet?.balance,
              )}
            </p>
          </div>
          <div className="rounded-lg border border-[#EAECF0] dark:border-[#FFFFFF1A] bg-white dark:bg-[#FFFFFF0A] p-4">
            <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
              DG Point Balance
            </p>
            <p className="mt-2 text-xl font-semibold dark:text-white">
              {formatMetricValue(
                selectedUser?.dg_point_wallet_balance ||
                  selectedUser?.dgpointwallet?.balance,
              )}
            </p>
          </div>
          <div className="rounded-lg border border-[#EAECF0] dark:border-[#FFFFFF1A] bg-white dark:bg-[#FFFFFF0A] p-4">
            <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Accumulated Points
            </p>
            <p className="mt-2 text-xl font-semibold dark:text-white">
              {formatMetricValue(selectedUser?.alltime_accumulated_points)}
            </p>
          </div>
          <div className="rounded-lg border border-[#EAECF0] dark:border-[#FFFFFF1A] bg-white dark:bg-[#FFFFFF0A] p-4">
            <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Monthly Points
            </p>
            <p className="mt-2 text-xl font-semibold dark:text-white">
              {formatMetricValue(selectedUser?.monthly_accumulated_points)}
            </p>
          </div>
        </div>

        <div className="mb-4 rounded-lg border border-[#EAECF0] dark:border-[#FFFFFF1A] bg-white dark:bg-[#FFFFFF0A] p-4">
          <div className="flex flex-col gap-3">
            <div>
              <p className="text-sm font-semibold dark:text-white">
                DG Coin Wallet Actions
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Credit or debit this user&apos;s DG Coin balance and log it as
                an admin transaction.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-[100px_minmax(140px,1fr)_auto_auto] gap-3">
              <input
                type="number"
                min="0.01"
                step="0.01"
                value={coinAdjustmentAmount}
                onChange={(e) => setCoinAdjustmentAmount(e.target.value)}
                placeholder="Amount"
                disabled={coinAdjustmentLoading}
                className="w-full rounded-md border border-[#D0D5DD] bg-white px-3 py-2 text-sm dark:border-[#3F3F46] dark:bg-[#27272A] dark:text-white"
              />
              <input
                type="text"
                value={coinAdjustmentDescription}
                onChange={(e) => setCoinAdjustmentDescription(e.target.value)}
                placeholder="Reason or note"
                disabled={coinAdjustmentLoading}
                className="w-full rounded-md border border-[#D0D5DD] bg-white px-3 py-2 text-sm dark:border-[#3F3F46] dark:bg-[#27272A] dark:text-white"
              />
              <button
                type="button"
                onClick={() => handleCoinAdjustment("credit")}
                disabled={coinAdjustmentLoading}
                className="rounded-md bg-[#12B76A] px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
              >
                {coinAdjustmentLoading ? "Processing..." : "Credit User"}
              </button>
              <button
                type="button"
                onClick={() => handleCoinAdjustment("debit")}
                disabled={coinAdjustmentLoading}
                className="rounded-md bg-[#D92D20] px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
              >
                {coinAdjustmentLoading ? "Processing..." : "Debit User"}
              </button>
            </div>
          </div>
        </div>

        {showTabs && tabItems.length > 0 && (
          <div className="rounded-lg p-[6px] bg-[#F4F4F5] dark:bg-BG-dark w-4/4 h-[42px] flex">
            {tabItems.map((item, index) => (
              <button
                key={index}
                className={`flex-1 py-[6px] px-[6px] bp:px-[10px] text-xs place-content-center ${
                  toggle === item
                    ? "inter-xs-semibold-dark rounded-lg font-inter dark:text-white bg-[#FCFCFD] dark:bg-[#FFFFFF1A]"
                    : "inter-xs-medium-toggleBG-dark dark:text-main-dark"
                }`}
                onClick={() => setToggle(item)}
              >
                {item}
              </button>
            ))}
          </div>
        )}

        {/* Render content based on the selected tab */}
        {renderContent()}
      </ModalContainer>
    </div>
  );
};

type PurchaseFilterState = {
  source: string;
  start_date: string;
  end_date: string;
  status: string;
  type: string;
};

type PurchasesFilterProps = {
  filters: PurchaseFilterState;
  loading: boolean;
  onFilterChange: (key: keyof PurchaseFilterState, value: string) => void;
  onClearFilters: () => void;
};

const PurchasesFilter = ({
  filters,
  loading,
  onFilterChange,
  onClearFilters,
}: PurchasesFilterProps) => {
  const statusOptions = [
    { value: "", label: "All Status" },
    { value: "0", label: "Successful" },
    { value: "1", label: "Pending" },
    { value: "2", label: "Paid" },
    { value: "3", label: "Queued" },
    { value: "6", label: "Failed" },
    { value: "5", label: "Reversed" },
  ];

  const typeOptions = [
    { value: "", label: "All Types" },
    { value: "0", label: "Data" },
    { value: "1", label: "Airtime" },
    { value: "2", label: "Cable" },
    { value: "3", label: "Electricity" },
    { value: "4", label: "Top-up" },
    { value: "5", label: "Game Card" },
    { value: "6", label: "Gift Card" },
    { value: "7", label: "DG Coins Purchase" },
    { value: "8", label: "DG Points Reward" },
    { value: "9", label: "Transfer To User" },
    { value: "10", label: "Transfer From User" },
    { value: "11", label: "Admin Withdrawal" },
    { value: "12", label: "Admin Payment" },
    { value: "13", label: "Reversal" },
  ];

  return (
    <div className="flex flex-col gap-2">
      <div className="inline-flex w-fit rounded-3xl border border-[#D0D5DD] dark:border-[#FFFFFF1A] overflow-hidden">
        {[
          { value: "active", label: "Active" },
          { value: "archived", label: "Archived" },
        ].map((option) => (
          <button
            key={option.value}
            type="button"
            disabled={loading}
            onClick={() => onFilterChange("source", option.value)}
            className={`px-4 py-2 text-xs bp:text-sm ${
              filters.source === option.value
                ? "bg-[#027EC2] text-white"
                : "bg-[#F4F4F5] text-gray-600 dark:bg-[#27272A] dark:text-white"
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>
      <div className="flex flex-col md:flex-row gap-2 w-full">
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={filters.start_date}
            onChange={(e) => onFilterChange("start_date", e.target.value)}
            disabled={loading}
            className="border p-2 bg-[#F4F4F5] w-[130px] h-[40px] rounded-3xl disabled:opacity-50 dark:bg-[#27272A] dark:text-white dark:border-[#3F3F46]"
            placeholder="Start Date"
          />
          <input
            type="date"
            value={filters.end_date}
            onChange={(e) => onFilterChange("end_date", e.target.value)}
            disabled={loading}
            className="border p-2 bg-[#F4F4F5] w-[130px] h-[40px] rounded-3xl disabled:opacity-50 dark:bg-[#27272A] dark:text-white dark:border-[#3F3F46]"
            placeholder="End Date"
          />
        </div>
        <div className="relative">
          <select
            value={filters.status}
            onChange={(e) => onFilterChange("status", e.target.value)}
            disabled={loading}
            className="border p-2 bg-[#F4F4F5] w-[200px] h-[40px] rounded-3xl appearance-none pr-8 dark:bg-[#27272A] dark:text-white dark:border-[#3F3F46]"
          >
            {statusOptions.map((option) => (
              <option
                key={option.value}
                value={option.value}
                className="bg-white dark:bg-[#27272A] text-black dark:text-white"
              >
                {option.label}
              </option>
            ))}
          </select>
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
            <Dropdown />
          </div>
        </div>
        <div className="relative">
          <select
            value={filters.type}
            onChange={(e) => onFilterChange("type", e.target.value)}
            disabled={loading}
            className="border p-2 bg-[#F4F4F5] w-[200px] h-[40px] rounded-3xl appearance-none pr-8 dark:bg-[#27272A] dark:text-white dark:border-[#3F3F46]"
          >
            {typeOptions.map((option) => (
              <option
                key={option.value}
                value={option.value}
                className="bg-white dark:bg-[#27272A] text-black dark:text-white"
              >
                {option.label}
              </option>
            ))}
          </select>
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
            <Dropdown />
          </div>
        </div>
        <button
          onClick={onClearFilters}
          disabled={loading}
          className="border border-[#D0D5DD] dark:border-[#FFFFFF1A] px-4 py-2 rounded-3xl text-xs bp:text-sm dark:text-white dark:bg-[#FFFFFF1A] hover:bg-gray-100 dark:hover:bg-[#FFFFFF33] disabled:opacity-50"
        >
          Clear
        </button>
      </div>
    </div>
  );
};
